// MarketPage — the organisation raster across the whole population (BUILD-BRIEF-R3 §4).
//
// Mast → five-cell rail (PROFILED · COVERAGE · TARGETS · SUB-SECTORS · TRIGGERS 30D) → two
// filter bands (a chip per raster block, then that block's values) → the grid → the Compare
// sheet when two rows are picked → Population, the directories the names came from.
//
// THE FOUR HONESTIES THIS PAGE OWES THE OPERATOR:
//
//   1. NOT PROFILED IS NOT ZERO COVERAGE. An organisation nobody has profiled has no coverage
//      figure at all, and the COVERAGE cell reads "not collected" until somebody is profiled. A
//      median of 0% would claim we looked at every cell and found them empty.
//   2. NOT SCORED IS NOT A LOW SCORE. The three axes and the composite are absent, not 1, until
//      `profile.compute` has scored the organisation. Such a row sorts LAST in both directions
//      (the server's NULLS LAST), never at the bottom of the ladder as though it had lost.
//   3. TRIGGERS ARE READ ONLY WHILE THEY ARE FRESH. TRIGGERS 30D counts trigger cells standing
//      true on a computation performed in the last 30 days; a row whose triggers were never
//      computed, or were computed longer ago, carries no figure. The rail's number is a real
//      count over those fresh computations — a zero there was counted.
//   4. THE RAIL DESCRIBES THE GRID. Every aggregate is measured over the FILTERED set, so the
//      rail and the rows below it can never be two different answers on one screen.
//
// THE DASH IN A NUMERIC COLUMN. Ten columns of the words "not collected" would make the grid
// unreadable, so the four axis columns and the coverage column render `fv--unknown` (the dotted
// under-line that means NOBODY COUNTED THIS) around an en dash, with "not collected" as the
// title. The wide text columns keep the words. What is never done is a zero, a 1 or a blank.
//
// LABELS COME FROM THE RASTER. `labelFor` in the console's current language — no page retypes a
// sub-sector, a band or a canton.
//
// ROUND 3b (BUILD-BRIEF-R3b §2b) adds the two rules that keep units from distorting the grid:
//
//   TOP-LEVEL ONLY  on by default, because the raster compares ORGANISATIONS. A department is
//                 not a badly-covered organisation.
//   FROM PARENT   with units shown, a unit's empty identity cells are drawn from its parent's
//                 held values and marked `from parent`. They are borrowed for reading, never
//                 restated as the unit's own facts — which is why they carry a ghost tag and
//                 no claim mark.
//
// ROUND 4 takes the DISCOVER verb away (BUILD-BRIEF-R4 §4). Growing the population is a task
// given to Hermes — "trouve les organisations du segment …, propose_organisation pour chacune
// avec la ligne qui la nomme" — not a form on this page. The Population zone still names the
// directories the record's names came from.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import CompareSheet from '../components/CompareSheet.js';
import EmptyState from '../components/EmptyState.js';
import { OrgLogo } from '../components/Identity.js';
import { TIER_TONE } from '../components/ProfileZone.js';
import SeriesZone from '../components/SeriesZone.js';
import {
  Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Tbl, Zone, ZoneHead,
  type RailTone,
} from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open as openRoute, useDeepLink, useRouteParams } from '../routes.js';
import { compare as compareOrgs, listMarket, whyScored, type MarketFilters } from '../api/market.js';
import { listDirectories } from '../api/directories.js';
import { listOrganisations } from '../api/organisations.js';
import { RASTER, labelFor, type RasterCell, type RasterLang } from '../../shared/raster.v1.js';
import type {
  CompareResponse, Directory, MarketResponse, MarketRow, OrgScore, OrgTier,
} from '../../shared/types.js';
import './MarketPage.de.js';
import './MarketPage.fr.js';

/** Rows drawn before the foot offers more. */
const PAGE_SIZE = 50;

/**
 * The most rows `GET /api/market` will return in one read (its own MAX_LIMIT). The page asks for
 * that many ONCE, ids only in effect, to count the units in the whole filtered set — see
 * `unitsInSet` below. A set larger than this is left uncounted rather than under-reported.
 */
const MARKET_MAX_ROWS = 500;

const TIERS: OrgTier[] = ['target', 'watch', 'park', 'exclude'];

/** The six filter dimensions of §4, each pointing at the raster cell it narrows on. */
const DIMENSIONS = [
  { name: 'subsector', block: 'identity', cell: 'subsector' },
  { name: 'canton', block: 'identity', cell: 'canton' },
  { name: 'fte_band', block: 'size_money', cell: 'fte_band' },
  { name: 'budget_band', block: 'size_money', cell: 'budget_band' },
  { name: 'procurement_regime', block: 'decision', cell: 'procurement_regime' },
  { name: 'tier', block: null, cell: null },
] as const;

type Dim = (typeof DIMENSIONS)[number]['name'];

const CELL_OF = new Map<string, RasterCell>();
for (const block of RASTER) for (const c of block.cells) CELL_OF.set(`${block.key}.${c.key}`, c);

function dimValues(dim: Dim): string[] {
  if (dim === 'tier') return TIERS;
  const d = DIMENSIONS.find((x) => x.name === dim)!;
  const cell = CELL_OF.get(`${d.block}.${d.cell}`);
  if (!cell) return [];
  return (cell.type === 'band' ? (cell.bands ?? []) : (cell.values ?? [])).map((v) => v.value);
}

/** Sortable grid columns, by the name the server's whitelist knows. */
type SortKey = 'name' | 'subsector' | 'canton' | 'fte_band' | 'budget_band' | 'tier'
  | 'fit' | 'timing' | 'access' | 'composite' | 'coverage' | 'triggers';

export default function MarketPage() {
  const t = useT();
  const { lang, fmtDate, fmtNum } = useLocale();
  const params = useRouteParams();

  const [filters, setFilters] = useState<Partial<Record<Dim, string>>>({});
  const [openDim, setOpenDim] = useState<Dim | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<SortKey>('composite');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [data, setData] = useState<MarketResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [picked, setPicked] = useState<number[]>([]);
  const [comparison, setComparison] = useState<CompareResponse | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);

  const [why, setWhy] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<Record<number, OrgScore | null>>({});
  const [whyError, setWhyError] = useState<string | null>(null);

  const [dirs, setDirs] = useState<Directory[] | null>(null);
  const [dirsError, setDirsError] = useState<string | null>(null);

  /**
   * THE TREE, read once (§2b). The raster compares ORGANISATIONS; a department is not a second
   * organisation with a worse coverage figure, so units are hidden by default and their empty
   * identity cells fall back to the parent's held values, marked as borrowed.
   *
   * `null` means nobody has answered: the chip then hides nothing, because a page that does not
   * know which rows are units must not pretend it does.
   */
  const [orgs, setOrgs] = useState<Map<number, { parent_id: number | null; name: string }> | null>(null);
  const [topLevelOnly, setTopLevelOnly] = useState(true);
  /**
   * Units in the WHOLE FILTERED SET, not in the page on screen (round-3b fix). The grid is paged
   * at fifty, so counting the units among the rows loaded answers a different question from the
   * one the chip asks — with 50 of 124 rows loaded it said "3" where the record holds four.
   *
   * `null` means nobody could count them: no population read answered, or the filtered set is
   * larger than one bounded read. The chip then shows no figure at all — an uncounted set is not
   * a set without units. The extra read disappears the day `/api/market` rows carry `parent_id`.
   */
  const [unitsInSet, setUnitsInSet] = useState<number | null>(null);

  // `#market?q=…&canton=VD&tier=target&organisation=7` — arrival intents the page then owns.
  useDeepLink(params, {
    q: (v) => { setQuery(v); setDebounced(v); },
    subsector: (v) => setFilters((f) => ({ ...f, subsector: v })),
    canton: (v) => setFilters((f) => ({ ...f, canton: v })),
    tier: (v) => setFilters((f) => ({ ...f, tier: v })),
    organisation: (v) => { const n = Number(v); if (Number.isInteger(n) && n > 0) setPicked([n]); },
  });

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body: MarketFilters = { ...filters, q: debounced || undefined, sort, dir, limit, offset: 0 };
      const res = await listMarket(body);
      setData(res);
      setError(null);
    } catch (err) {
      // A failed read leaves the LAST answer standing rather than blanking every figure — but
      // it says so, and never invents a zero.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [filters, debounced, sort, dir, limit]);

  useEffect(() => { void load(); }, [load]);

  // A different filter, sort or search is a different set: start again at one page.
  useEffect(() => { setLimit(PAGE_SIZE); }, [filters, debounced, sort, dir]);

  useEffect(() => {
    let live = true;
    listDirectories()
      .then((r) => { if (live) { setDirs(r.items); setDirsError(null); } })
      .catch((err) => { if (live) { setDirs(null); setDirsError(err instanceof Error ? err.message : String(err)); } });
    return () => { live = false; };
  }, []);

  // One read of the population's own rows, for the two things the market payload does not
  // carry: which organisation is a unit of which, and the parent's name.
  useEffect(() => {
    let live = true;
    listOrganisations({})
      .then((r) => {
        if (!live) return;
        setOrgs(new Map(r.items.map((o) => [o.id, { parent_id: o.parent_id ?? null, name: o.name }])));
      })
      .catch(() => { if (live) setOrgs(null); });
    return () => { live = false; };
  }, []);

  /**
   * Count the units across the filtered set: one unpaged read of the same filters, mapped through
   * the population's parent links. It runs on a filter or search change, never on "show more" —
   * which rows are loaded does not change how many of them are units.
   */
  useEffect(() => {
    let live = true;
    setUnitsInSet(null);
    listMarket({ ...filters, q: debounced || undefined, limit: MARKET_MAX_ROWS, offset: 0 })
      .then((r) => {
        if (!live) return;
        // A set the read could not hold whole cannot be counted honestly.
        if (r.items.length < r.total) { setUnitsInSet(null); return; }
        // The row's own parent link when the server ships one, the population read otherwise;
        // with neither, nothing is known about the tree and the count stays uncounted.
        let known = orgs !== null;
        const units = r.items.filter((row) => {
          const own = (row as MarketRow & { parent_id?: number | null }).parent_id;
          if (typeof own === 'number') { known = true; return true; }
          if (own === null) { known = true; return false; }
          return (orgs?.get(row.organisation_id)?.parent_id ?? null) !== null;
        }).length;
        setUnitsInSet(known ? units : null);
      })
      .catch(() => { if (live) setUnitsInSet(null); });
    return () => { live = false; };
  }, [filters, debounced, orgs]);

  useEffect(() => {
    if (picked.length !== 2) { setComparison(null); setCompareError(null); return; }
    let live = true;
    setComparison(null);
    compareOrgs(picked[0], picked[1])
      .then((r) => { if (live) { setComparison(r); setCompareError(null); } })
      .catch((err) => { if (live) setCompareError(err instanceof Error ? err.message : String(err)); });
    return () => { live = false; };
  }, [picked]);

  const openWhy = useCallback(async (organisationId: number) => {
    setWhy((cur) => (cur === organisationId ? null : organisationId));
    if (breakdown[organisationId] !== undefined) return;
    try {
      const res = await whyScored(organisationId);
      setBreakdown((b) => ({ ...b, [organisationId]: res.score }));
      setWhyError(null);
    } catch (err) {
      setWhyError(err instanceof Error ? err.message : String(err));
    }
  }, [breakdown]);

  /** Picking a third organisation drops the first: a comparison has exactly two sides. */
  const pick = (id: number) => setPicked((cur) => (
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(-2)
  ));

  const setFilter = (dim: Dim, value: string | null) => {
    setFilters((f) => {
      const next = { ...f };
      if (value === null) delete next[dim];
      else next[dim] = value;
      return next;
    });
  };

  const sortBy = (key: SortKey) => {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const agg = data?.aggregates ?? null;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeFilters = Object.keys(filters).length + (debounced ? 1 : 0);
  const nothingProfiled = !!agg && agg.profiled === 0;

  /** A row's parent: the market payload's own field when it carries one, otherwise the
   *  population read. Null means top-level — or, while `orgs` is still null, unknown. */
  const parentIdOf = useCallback((row: MarketRow): number | null => {
    const own = (row as MarketRow & { parent_id?: number | null }).parent_id;
    if (typeof own === 'number') return own;
    return orgs?.get(row.organisation_id)?.parent_id ?? null;
  }, [orgs]);

  const shown = useMemo(
    () => (topLevelOnly ? items.filter((row) => parentIdOf(row) === null) : items),
    [items, topLevelOnly, parentIdOf],
  );
  /** Rows the chip took off the SCREEN. Page-scoped by nature: you can only hide what you hold. */
  const hiddenOnPage = items.length - shown.length;
  /**
   * Units the chip hides across the whole filtered set — the figure the chip and the note print.
   * With every row loaded the page IS the set; otherwise the unpaged count above answers, and
   * `null` when nobody counted. Never the page's number dressed up as the set's.
   */
  const hiddenUnits: number | null = !topLevelOnly
    ? 0
    : items.length >= total ? hiddenOnPage : unitsInSet;
  /**
   * Organisations the grid shows in the filtered set: the total less its units. Null when the
   * units were not counted — the head then falls back to the rows on screen and says so.
   */
  const gridTotal: number | null = hiddenUnits === null ? null : total - hiddenUnits;
  /** The rows already on screen, by id — where a unit's borrowed identity cells come from. */
  const byId = useMemo(() => new Map(items.map((r) => [r.organisation_id, r])), [items]);

  const dimLabel = useCallback((dim: Dim): string => {
    if (dim === 'tier') return t('Tier');
    const d = DIMENSIONS.find((x) => x.name === dim)!;
    return labelFor(lang as RasterLang, d.block!, d.cell!);
  }, [lang, t]);

  const valueLabel = useCallback((dim: Dim, value: string): string => {
    if (dim === 'tier') return t(value);
    const d = DIMENSIONS.find((x) => x.name === dim)!;
    return labelFor(lang as RasterLang, d.block!, d.cell!, value);
  }, [lang, t]);

  const rail = useMemo(() => railCells(agg, t, fmtNum, loading, error), [agg, t, fmtNum, loading, error]);

  return (
    <PageShell>
      <Mast
        kicker={t('Organisation raster · v1')}
        title={t('Market')}
        sub={agg ? (
          <>
            <Fig>{fmtNum(total)}</Fig> {t('organisations')}<Sep />
            <Fig>{fmtNum(agg.profiled)}</Fig> {t('profiled')}<Sep />
            {t('every organisation against one raster of comparable cells')}
          </>
        ) : t('Every organisation against one raster of comparable cells — who is a target, and why.')}
      />

      <Rail
        after={
          <>
            <div className="instr-band">
              <div className="filter-chips">
                {DIMENSIONS.map((d) => {
                  const chosen = filters[d.name];
                  return (
                    <button
                      key={d.name}
                      type="button"
                      className={`chip${openDim === d.name || chosen ? ' active' : ''}`}
                      aria-pressed={openDim === d.name}
                      onClick={() => setOpenDim((o) => (o === d.name ? null : d.name))}
                    >
                      {dimLabel(d.name)}
                      {chosen && <> · {valueLabel(d.name, chosen)}</>}
                    </button>
                  );
                })}
                {/* Units are departments, sites and programmes (§2b). The raster compares
                    ORGANISATIONS, so they are out by default; turning the chip off shows them
                    with their identity cells borrowed from the parent and marked as borrowed. */}
                <button
                  type="button"
                  className={`chip${topLevelOnly ? ' active' : ''}`}
                  aria-pressed={topLevelOnly}
                  title={t('A unit is a department, a site or a programme under another organisation. With this on, the raster compares organisations only.')}
                  onClick={() => setTopLevelOnly((v) => !v)}
                >
                  {t('Top-level only')}
                  {topLevelOnly && hiddenUnits !== null && hiddenUnits > 0 && <> <Fig>{fmtNum(hiddenUnits)}</Fig></>}
                </button>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    className="chip"
                    onClick={() => { setFilters({}); setQuery(''); setDebounced(''); setOpenDim(null); }}
                  >
                    {t('Clear the filter')}
                  </button>
                )}
              </div>
              <div className="search-wrapper ml-auto w-full max-w-sm">
                <input
                  className="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('Name, legal name, ref…')}
                  aria-label={t('Search the population')}
                />
              </div>
            </div>
            {openDim && (
              <div className="instr-band">
                <div className="filter-chips">
                  <span className="u-label self-center">{dimLabel(openDim)}</span>
                  <button
                    type="button"
                    className={`chip${filters[openDim] ? '' : ' active'}`}
                    onClick={() => setFilter(openDim, null)}
                  >
                    {t('All')}
                  </button>
                  {dimValues(openDim).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`chip${filters[openDim] === v ? ' active' : ''}`}
                      aria-pressed={filters[openDim] === v}
                      onClick={() => setFilter(openDim, filters[openDim] === v ? null : v)}
                    >
                      {valueLabel(openDim, v)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        }
      >
        {rail}
      </Rail>

      {/* ── the grid ─────────────────────────────────────────────────────── */}
      <Zone id="grid">
        <ZoneHead
          label={t('Organisations')}
          count={data
            ? gridTotal !== null
              ? <Fig>{fmtNum(gridTotal)}</Fig>
              : <Fig title={t('counted among the rows loaded; the units in the rest of the set were not counted')}>{fmtNum(shown.length)}</Fig>
            : undefined}
          note={t('pick two rows to compare their rasters')}
          endNote={picked.length === 1
            ? t('one picked · pick a second to compare')
            : hiddenUnits !== null && hiddenUnits > 0
              ? t('{n} units hidden by “Top-level only”', { n: fmtNum(hiddenUnits) })
              : hiddenUnits === null && hiddenOnPage > 0
                ? t('{n} units hidden among the rows loaded', { n: fmtNum(hiddenOnPage) })
                : undefined}
        />

        {error && !data ? (
          <EmptyState
            kind="error"
            title={t('The market did not answer')}
            detail={<>GET /api/market — {error}</>}
            onRetry={() => { void load(); }}
          />
        ) : !data ? (
          <p className="zone-empty">{t('Loading…')}</p>
        ) : shown.length === 0 ? (
          <EmptyState
            kind={activeFilters || hiddenOnPage > 0 ? 'no-match' : 'empty'}
            title={activeFilters || hiddenOnPage > 0 ? t('Nothing matches') : t('Nothing here yet')}
            detail={hiddenOnPage > 0 && !activeFilters
              ? t('Every organisation these filters match is a unit of another one, and “Top-level only” is on.')
              : activeFilters
                ? t('No organisation on the record matches these filters.')
                : t('The record holds no active organisation. Organisations arrive from the seed import, from a directory monitor, or from a proposal you hold.')}
            onClear={activeFilters || hiddenOnPage > 0
              ? () => { setFilters({}); setQuery(''); setDebounced(''); setTopLevelOnly(false); }
              : undefined}
            clearLabel={t('Clear the filter')}
          />
        ) : (
          <>
            {nothingProfiled && (
              <p className="zone-empty">
                {t('No organisation holds a single raster cell yet, so every cell below reads "not collected". Open an organisation and use "Fill profile": it starts a research run against the raster, and every value it reads comes back as a proposal with its verbatim quote.')}
              </p>
            )}
            {error && (
              <p className="zone-empty">{t('The last refresh failed ({error}). The figures below are the previous answer.', { error })}</p>
            )}
            <Sheet clip>
              <div style={{ overflowX: 'auto' }}>
                <Tbl>
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} aria-label={t('Pick')} />
                      <Th k="name" sort={sort} dir={dir} onSort={sortBy}>{t('Organisation')}</Th>
                      <Th k="subsector" sort={sort} dir={dir} onSort={sortBy}>{dimLabel('subsector')}</Th>
                      <Th k="canton" sort={sort} dir={dir} onSort={sortBy}>{dimLabel('canton')}</Th>
                      <Th k="fte_band" sort={sort} dir={dir} onSort={sortBy}>{dimLabel('fte_band')}</Th>
                      <Th k="budget_band" sort={sort} dir={dir} onSort={sortBy}>{dimLabel('budget_band')}</Th>
                      <Th k="tier" sort={sort} dir={dir} onSort={sortBy}>{t('Tier')}</Th>
                      <Th k="fit" sort={sort} dir={dir} onSort={sortBy} num>{t('Fit')}</Th>
                      <Th k="timing" sort={sort} dir={dir} onSort={sortBy} num>{t('Timing')}</Th>
                      <Th k="access" sort={sort} dir={dir} onSort={sortBy} num>{t('Access')}</Th>
                      <Th k="composite" sort={sort} dir={dir} onSort={sortBy} num>{t('Composite')}</Th>
                      <Th k="coverage" sort={sort} dir={dir} onSort={sortBy} num>{t('Coverage')}</Th>
                      <th className="num">{t('Why')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => (
                      <GridRow
                        key={row.organisation_id}
                        row={row}
                        // A unit's own empty identity cells are drawn from its parent's HELD
                        // values and marked as borrowed — never restated as the unit's facts.
                        parent={(() => {
                          const pid = parentIdOf(row);
                          if (pid === null) return null;
                          return { row: byId.get(pid) ?? null, name: orgs?.get(pid)?.name ?? null };
                        })()}
                        lang={lang as RasterLang}
                        picked={picked.includes(row.organisation_id)}
                        onPick={() => pick(row.organisation_id)}
                        onOpen={() => openRoute(ROUTES.org({ id: row.organisation_id }))}
                        whyOpen={why === row.organisation_id}
                        onWhy={() => { void openWhy(row.organisation_id); }}
                        score={breakdown[row.organisation_id]}
                        whyError={whyError}
                        fmtNum={fmtNum}
                      />
                    ))}
                  </tbody>
                </Tbl>
              </div>
            </Sheet>
            {items.length < total && (
              <div className="instr-band" style={{ borderTop: 0, background: 'none', paddingLeft: 0 }}>
                <Lnk onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                  {t('Show {n} more', { n: Math.min(PAGE_SIZE, total - items.length) })}
                </Lnk>
                <span className="zone-note">
                  {t('{shown} of {total}', { shown: fmtNum(items.length), total: fmtNum(total) })}
                </span>
              </div>
            )}
          </>
        )}
      </Zone>

      {/* ── the comparison ───────────────────────────────────────────────── */}
      {picked.length === 2 && (
        <Zone id="compare">
          <ZoneHead
            label={t('Compare')}
            note={t('the same raster, cell by cell — a row empty on both sides is a fact about our coverage')}
          />
          <CompareSheet
            comparison={comparison}
            error={compareError}
            loading={!comparison && !compareError}
            onClose={() => setPicked([])}
          />
        </Zone>
      )}

      {/* ── where the names came from ────────────────────────────────────── */}
      <Zone id="population">
        <ZoneHead
          label={t('Population')}
          count={dirs ? <Fig>{fmtNum(dirs.length)}</Fig> : undefined}
          note={t('the federation lists and cantonal registers the names came from')}
          route={
            <button
              type="button"
              className="zone-route"
              onClick={() => openRoute(ROUTES.review({ kind: 'organisation', status: 'unheld' }))}
            >
              {t('Review')} →
            </button>
          }
        />
        {dirsError ? (
          <p className="zone-empty">{t('The directories could not be read: {error}', { error: dirsError })}</p>
        ) : !dirs ? (
          <p className="zone-empty">{t('Loading…')}</p>
        ) : dirs.length === 0 ? (
          <p className="zone-empty">
            {t('No directory is registered yet. A directory is a federation member list — AVOP, Fachverband Sucht, the DGCS list of établissements socio-éducatifs — whose unknown names arrive as organisation proposals with the list line as their verbatim quote.')}
          </p>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Directory')}</th>
                  <th>{t('Federation')}</th>
                  <th className="num">{t('Members seen')}</th>
                  <th className="num">{t('Waiting as proposals')}</th>
                  <th className="num">{t('Last import')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {dirs.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.name}
                      {d.url && <span className="d"> · <Ref>{d.url}</Ref></span>}
                    </td>
                    <td>{d.federation ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                    <td className="num">
                      {/* NULL means nobody has read the list; it has not seen zero members. */}
                      {d.members_seen === null || d.members_seen === undefined
                        ? <span className="fv--unknown">{t('not collected')}</span>
                        : <Fig className={d.members_seen === 0 ? 'fv--nil' : undefined}>{fmtNum(d.members_seen)}</Fig>}
                    </td>
                    <td className="num">
                      {d.pending_organisations === null || d.pending_organisations === undefined
                        ? <span className="fv--unknown">{t('not collected')}</span>
                        : <Fig className={d.pending_organisations === 0 ? 'fv--nil' : undefined}>{fmtNum(d.pending_organisations)}</Fig>}
                    </td>
                    <td className="num">
                      {d.last_import_at
                        ? <Fig>{fmtDate(d.last_import_at)}</Fig>
                        : <span className="fv--unknown">{t('never imported')}</span>}
                    </td>
                    <td>
                      {d.last_outcome
                        ? <Tag tone={d.last_outcome === 'error' || d.last_outcome === 'refused' ? 'broken' : d.last_outcome === 'signal' ? 'attend' : 'neutral'}>{t(d.last_outcome)}</Tag>
                        : <span className="fv--unknown">{t('never run')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── public statistics about the market — not one organisation's own record ────────── */}
      <SeriesZone />

    </PageShell>
  );
}

// ── the rail ────────────────────────────────────────────────────────────────

/**
 * Five cells, measured over the FILTERED set. COVERAGE is the one that must not lie: a null
 * median means nobody is profiled, and reads "not collected" rather than 0%.
 */
function railCells(
  agg: MarketResponse['aggregates'] | null,
  t: (key: string, vars?: Record<string, string | number>) => string,
  fmtNum: (n: number | string | null | undefined) => string,
  loading: boolean,
  error: string | null,
) {
  if (!agg) {
    const detail = error ? t('the market did not answer') : loading ? t('counting the population…') : t('nothing has answered yet');
    return (
      <>
        {['Profiled', 'Coverage', 'Targets', 'Sub-sectors', 'Triggers 30d'].map((label) => (
          <RailCell key={label} label={t(label)} value={t('not collected')} tone="unknown" detail={detail} />
        ))}
      </>
    );
  }
  const tone = (n: number): RailTone => (n === 0 ? 'nil' : 'ink');
  return (
    <>
      <RailCell
        label={t('Profiled')}
        value={<Fig>{fmtNum(agg.profiled)}</Fig>}
        tone={tone(agg.profiled)}
        detail={agg.profiled
          ? t('organisations holding at least one profile cell')
          : t('nobody has filled a single raster cell')}
      />
      <RailCell
        label={t('Coverage')}
        value={agg.coverage_median_pct === null ? t('not collected') : <Fig>{fmtNum(agg.coverage_median_pct)}</Fig>}
        unit={agg.coverage_median_pct === null ? undefined : '%'}
        tone={agg.coverage_median_pct === null ? 'unknown' : agg.coverage_median_pct === 0 ? 'nil' : 'ink'}
        detail={agg.coverage_median_pct === null
          ? t('no organisation is profiled, so there is no median to take')
          : t('median share of the comparable cells that are filled')}
      />
      <RailCell
        label={t('Targets')}
        value={<Fig>{fmtNum(agg.targets)}</Fig>}
        tone={agg.targets ? 'ok' : 'nil'}
        detail={t('{w} watch · {p} park · {e} exclude', {
          w: fmtNum(agg.by_tier.watch), p: fmtNum(agg.by_tier.park), e: fmtNum(agg.by_tier.exclude),
        })}
      />
      <RailCell
        label={t('Sub-sectors')}
        value={<Fig>{fmtNum(agg.subsectors)}</Fig>}
        tone={tone(agg.subsectors)}
        detail={agg.subsectors
          ? t('distinct sub-sectors on the record')
          : t('no sub-sector cell has been filled')}
      />
      <RailCell
        label={t('Triggers 30d')}
        value={<Fig>{fmtNum(agg.triggers_30d)}</Fig>}
        tone={agg.triggers_30d ? 'attend' : 'nil'}
        detail={t('trigger cells standing true on a computation from the last 30 days')}
      />
    </>
  );
}

// ── the grid ────────────────────────────────────────────────────────────────

function Th({ k, sort, dir, onSort, num, children }: {
  k: SortKey;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  num?: boolean;
  children: ReactNode;
}) {
  const active = sort === k;
  return (
    <th className={num ? 'num' : undefined} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className="lnk"
        style={{ font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: 'inherit' }}
        onClick={() => onSort(k)}
      >
        {children}{active ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
}

/** The dotted dash: nobody counted this. Never a zero, never a blank cell. */
function Uncounted({ label }: { label: string }) {
  return <span className="fv--unknown" title={label}>–</span>;
}

function GridRow({ row, parent, lang, picked, onPick, onOpen, whyOpen, onWhy, score, whyError, fmtNum }: {
  row: MarketRow;
  /** The row this one is a unit of, when it is one: its market row (for the borrowed cells) and
   *  its name (for the title that says whose values these are). Null for a top-level row. */
  parent: { row: MarketRow | null; name: string | null } | null;
  lang: RasterLang;
  picked: boolean;
  onPick: () => void;
  onOpen: () => void;
  whyOpen: boolean;
  onWhy: () => void;
  score: OrgScore | null | undefined;
  whyError: string | null;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  const t = useT();
  const none = <span className="fv--unknown">{t('not collected')}</span>;
  const uncounted = t('not collected');

  /**
   * ONE IDENTITY CELL, and the whole of the from-parent rule (§2b).
   *
   * A unit's own value wins. Where the unit holds nothing and its parent does, the PARENT's
   * value is shown with a ghost "from parent" tag — it is borrowed for reading, never restated
   * as a fact about the unit, and it carries no claim mark of its own because the unit has made
   * no claim. Where neither holds anything, the cell says nobody collected it.
   */
  const identity = (read: (r: MarketRow) => string | null): ReactNode => {
    const own = read(row);
    if (own) return own;
    const borrowed = parent?.row ? read(parent.row) : null;
    if (!borrowed) return none;
    return (
      <>
        {borrowed}{' '}
        <Tag tone="ghost" title={parent?.name
          ? t('held by {name}, this unit’s parent — not a fact about the unit', { name: parent.name })
          : t('held by this unit’s parent — not a fact about the unit')}
        >
          {t('from parent')}
        </Tag>
      </>
    );
  };

  return (
    <>
      <tr>
        <td>
          <input
            type="checkbox"
            checked={picked}
            onChange={onPick}
            aria-label={t('Pick {name} for the comparison', { name: row.name })}
          />
        </td>
        <td>
          <span className="idcell">
            <OrgLogo id={row.organisation_id} name={row.name} />
            <span>
              <Lnk onClick={onOpen}>{row.name}</Lnk>
              <span className="d"> · <Ref>{row.ref}</Ref></span>
              {parent && (
                <span className="d"> · {t('unit of')} {parent.name ?? (parent.row ? parent.row.name : t('another organisation'))}</span>
              )}
            </span>
          </span>
        </td>
        <td>
          {identity((r) => (r.subsector.length
            ? r.subsector.map((s) => labelFor(lang, 'identity', 'subsector', s)).join(' · ')
            : null))}
        </td>
        <td>{identity((r) => (r.canton ? labelFor(lang, 'identity', 'canton', r.canton) : null))}</td>
        <td>{identity((r) => (r.fte_band ? labelFor(lang, 'size_money', 'fte_band', r.fte_band) : null))}</td>
        <td>{identity((r) => (r.budget_band ? labelFor(lang, 'size_money', 'budget_band', r.budget_band) : null))}</td>
        <td>{row.tier ? <Tag tone={TIER_TONE[row.tier]}>{t(row.tier)}</Tag> : none}</td>
        <td className="num">{row.fit === null ? <Uncounted label={uncounted} /> : <Fig>{row.fit}</Fig>}</td>
        <td className="num">{row.timing === null ? <Uncounted label={uncounted} /> : <Fig>{row.timing}</Fig>}</td>
        <td className="num">{row.access === null ? <Uncounted label={uncounted} /> : <Fig>{row.access}</Fig>}</td>
        <td className="num">{row.composite === null ? <Uncounted label={uncounted} /> : <Fig>{row.composite}</Fig>}</td>
        <td className="num">
          {row.coverage_pct === null
            ? <Uncounted label={uncounted} />
            : <Fig unit="%" className={row.coverage_pct === 0 ? 'fv--nil' : undefined}>{fmtNum(row.coverage_pct)}</Fig>}
        </td>
        <td className="num">
          {row.tier
            ? <Lnk onClick={onWhy}>{whyOpen ? t('hide') : t('Why')}</Lnk>
            : <Uncounted label={t('not scored')} />}
        </td>
      </tr>
      {whyOpen && (
        <tr>
          <td colSpan={13}>
            {score === undefined ? (
              <span className="zone-note">{whyError ?? t('Loading…')}</span>
            ) : score === null ? (
              <span className="zone-note">{t('This organisation has not been scored, so no rule has fired for it.')}</span>
            ) : score.breakdown.length === 0 ? (
              <span className="zone-note">
                {t('No rule fired. The composite is the product of the three floors — the honest score for an organisation nothing is known about.')}
              </span>
            ) : (
              <div className="fgroup">
                <div className="fgroup-h u-label">
                  {t('Why')} · {t(score.tier)} · <Fig>{score.composite}</Fig>
                  <span className="zone-note"> · {t('fit')} <Fig>{score.fit}</Fig> · {t('timing')} <Fig>{score.timing}</Fig> · {t('access')} <Fig>{score.access}</Fig></span>
                </div>
                {score.breakdown.map((b, i) => (
                  <div className="fact" key={i} style={{ gridTemplateColumns: '90px minmax(0,1fr) auto' }}>
                    <span className="fk">{t(b.axis)} · <Ref>{b.rule}</Ref></span>
                    <span>{b.because}</span>
                    <span className="fmeta"><Fig>{b.points > 0 ? `+${b.points}` : b.points}</Fig></span>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
